import { clearTokens, emitAuthExpired, getAccessToken, getRefreshToken, setTokens } from './tokens';

/**
 * The backend, from the browser.
 *
 * Every business route lives under `VITE_API_BASE_URL` (`…/v1`); the two
 * probes `/health` and `/ready` sit at the root beside it. A call here reads
 * like the Swagger line it implements: `api('/admin/stats')`.
 *
 * Errors surface as {@link ApiError} carrying the backend's
 * `{ statusCode, error, message, requestId }`. The `requestId` is what the
 * backend team asks for when a response disagrees with the spec, so it is
 * kept and shown, never swallowed.
 *
 * A 401 on a business route means the access token expired: refresh ONCE —
 * single-flight, because the backend rotates refresh tokens and a second
 * concurrent refresh with the same token reads as a replay that revokes the
 * whole family — then retry the call once. A refresh the backend rejects ends
 * the session: tokens are cleared and `onAuthExpired` fires.
 */

const raw = import.meta.env.VITE_API_BASE_URL ?? 'https://dravizo.uz/v1';
export const API_BASE_URL = raw.replace(/\/+$/, '');
export const API_ROOT_URL = API_BASE_URL.replace(/\/v\d+$/, '');
export const DEVICE_NAME = 'Dravizo Admin (web)';

const TIMEOUT_MS = 30_000;

export interface ApiErrorBody {
  statusCode: number;
  error: string;
  message: string;
  requestId?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error implements ApiErrorBody {
  readonly statusCode: number;
  readonly error: string;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.error = body.error;
    this.requestId = body.requestId;
    this.details = body.details;
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

/** One line for a toast: the message, and the request id when the backend gave one. */
export function describeError(err: unknown): string {
  if (ApiError.is(err)) return err.requestId ? `${err.message} (requestId: ${err.requestId})` : err.message;
  if (err instanceof DOMException && err.name === 'AbortError') return 'Бэкенд не ответил за 30 секунд';
  if (err instanceof TypeError) return 'Нет связи с бэкендом (сеть или CORS)';
  return err instanceof Error ? err.message : String(err);
}

type QueryValue = string | number | boolean | null | undefined;

export interface ApiInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Pre-auth calls: a 401 here is bad credentials, not an expired session. */
  skipAuthRefresh?: boolean;
}

const NO_REFRESH_PREFIXES = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/forgot-password', '/auth/reset-password', '/auth/verify-email', '/auth/invitations'];

function toQuery(query?: Record<string, QueryValue>): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== null && v !== undefined) params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try { return JSON.parse(text) as unknown; } catch { return text; }
}

function toApiError(status: number, payload: unknown): ApiError {
  if (payload && typeof payload === 'object') {
    const r = payload as Record<string, unknown>;
    const m = r.message;
    return new ApiError({
      statusCode: typeof r.statusCode === 'number' ? r.statusCode : status,
      error: typeof r.error === 'string' ? r.error : 'Error',
      message: Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : `Запрос завершился с кодом ${status}`,
      requestId: typeof r.requestId === 'string' ? r.requestId : undefined,
      details: r,
    });
  }
  return new ApiError({ statusCode: status, error: 'Error', message: typeof payload === 'string' && payload ? payload : `Запрос завершился с кодом ${status}` });
}

function withTimeout(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

async function send(path: string, init: ApiInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...init.headers };
  let body: string | undefined;
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}${toQuery(init.query)}`, {
    method: init.method ?? 'GET',
    headers,
    body,
    signal: withTimeout(init.signal),
  });
}

// ── Refresh, single-flight ────────────────────────────────────────────────────

let refreshing: Promise<string> | null = null;

/** Trade the refresh token for a new pair; concurrent callers share one flight. */
export function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing;
  refreshing = doRefresh().finally(() => { refreshing = null; });
  return refreshing;
}

async function doRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearTokens();
    emitAuthExpired();
    throw new ApiError({ statusCode: 401, error: 'Unauthorized', message: 'Сессии нет' });
  }
  let res: Response;
  try {
    res = await send('/auth/refresh', { method: 'POST', body: { refreshToken }, headers: { 'X-Device-Name': DEVICE_NAME } }, null);
  } catch (err) {
    // A network blip must not log anyone out; a hung refresh cannot be recovered from.
    if (err instanceof DOMException && err.name === 'TimeoutError') { clearTokens(); emitAuthExpired(); }
    throw err;
  }
  if (!res.ok) {
    clearTokens();
    emitAuthExpired();
    throw toApiError(res.status, await parseBody(res));
  }
  const pair = (await res.json()) as { accessToken: string; refreshToken: string };
  setTokens(pair);
  return pair.accessToken;
}

// ── The call ──────────────────────────────────────────────────────────────────

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const refreshable = !init.skipAuthRefresh && !NO_REFRESH_PREFIXES.some((p) => path.startsWith(p));

  // After a reload there is a refresh token and no access token: get one
  // first rather than knowingly sending a request that will 401.
  let token = getAccessToken();
  if (!token && refreshable && getRefreshToken()) token = await refreshAccessToken();

  let res = await send(path, init, token);
  if (res.status === 401 && refreshable) {
    let fresh: string | null = null;
    try { fresh = await refreshAccessToken(); } catch { /* fall through with the original 401 */ }
    if (fresh) res = await send(path, init, fresh);
  }

  const payload = await parseBody(res);
  if (!res.ok) throw toApiError(res.status, payload);
  return payload as T;
}

/**
 * Read a collection out of a response whose envelope we cannot pin down: this
 * backend answers some lists as a bare array and others as `{ data: [...] }`,
 * and which is which has changed under the PMS once already. Fails naming the
 * endpoint when it is neither — a quiet `[]` would turn a broken endpoint into
 * an empty screen, the harder bug to find of the two.
 */
export function asList<T>(payload: unknown, endpoint: string): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown }).data;
    if (Array.isArray(data)) return data as T[];
  }
  if (payload === null || payload === undefined) return [];
  throw new Error(`${endpoint} ответил не списком, а ${typeof payload}`);
}
